import { Label as RadixLabel } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { forwardRef, useContext, useEffect } from 'react';

import { useId } from '../../core/hooks/useId.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { MenuGroupContext } from './contexts/MenuGroupContext.js';
import * as styles from './Menu.sty.js';

/**
 * @public
 */
export interface MenuLabelProps
  extends WithChildren,
    AriaLabelingProps,
    DOMProps,
    StylingProps,
    DataTestId {}

/**
 * Used to render a label. It won't be focusable using arrow keys.
 *
 * When rendered inside a `Menu.Group`, it automatically becomes that group's
 * accessible name.
 * @public
 */
export const Label = /* @__PURE__ */ forwardRef<HTMLDivElement, MenuLabelProps>(
  (
    {
      children,
      id,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    },
    forwardedRef,
  ) => {
    const group = useContext(MenuGroupContext);
    const fallbackId = useId();
    // Only labels inside a group need a generated id — it is what the group
    // points its `aria-labelledby` at. Each label gets its own, so a group with
    // several labels never emits the same id twice.
    const effectiveId = id ?? (group ? fallbackId : undefined);
    const registerLabel = group?.registerLabel;

    useEffect(() => {
      if (!registerLabel || effectiveId === undefined) {
        return;
      }
      return registerLabel(effectiveId);
    }, [registerLabel, effectiveId]);

    return (
      <RadixLabel
        ref={forwardedRef}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        id={effectiveId}
        className={clsx(styles.label, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
      >
        {children}
      </RadixLabel>
    );
  },
);

(Label as typeof Label & { displayName: string }).displayName = 'Menu.Label';
